<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />
    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main>
        <div class="mx-auto px-12 py-10">
          <div class="flex items-start justify-between">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center gap-3">
                <h1
                  tabindex="-1"
                  class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight"
                  ref="initialFocus"
                >
                  {{ t("pages.groups.heading.title") }}
                </h1>
                <a
                  class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  href="https://docs.knocknoc.io/books/admin-guide/page/create-groups"
                  target="_blank"
                  :aria-label="`${t('pages.groups.docs')} (${t('common.opensInNewTab')})`"
                >
                  {{ t("common.docs") }}
                  <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                  <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                </a>
              </div>

              <div class="max-w-2xl">
                <p class="text-base leading-relaxed text-muted-foreground">
                  {{ t("pages.groups.heading.description") }}
                </p>
              </div>
            </div>

            <div class="flex flex-shrink-0 flex-col items-end">
              <button
                type="button"
                @click="
                  closeModal();
                  openModal();
                "
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              >
                {{ t("pages.groups.heading.actions.create") }}
              </button>
            </div>
          </div>

          <div class="mt-8 flow-root min-h-screen overflow-x-auto">
            <AutoTable :columns="groupsTableColumns" :rows="filterData">
              <template #default>
                <TableRow>
                  <TableCell :colspan="groupsTableColumns?.length" class="text-center text-muted-foreground">
                    <div class="flex items-center justify-center gap-2">
                      <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                      <span>
                        {{ t("common.getStarted", { button: t("msg.createGroup") }) }}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </template>

              <template #col-Type="{ row: group }">
                {{ groupTypes(t)[group.Type]() }}
              </template>

              <template #col-Knocs="{ row: group }">
                {{ group?.ACLIDs?.length || 0 }}
              </template>

              <template #col-Users="{ row: group }">
                {{ group?.UserIDs?.length || 0 }}
              </template>

              <template #col-Edit="{ row: group }">
                <Menu as="div" class="inline-block">
                  <MenuButton
                    class="inline-flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                  >
                    {{ t("msg.actions") }}
                    <ChevronDownIcon
                      class="-mr-1 ml-2 h-5 w-5 text-violet-200 hover:text-violet-100"
                      aria-hidden="true"
                    />
                  </MenuButton>

                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <MenuItems
                      anchor="bottom end"
                      class="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-[#e05454] rounded-md bg-background shadow-lg ring-1 ring-[#e05454] focus:outline-none"
                    >
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentGroup = group;
                              openModal();
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                          >
                            <PencilSquareIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.edit") }}
                          </button>
                        </MenuItem>
                      </div>
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentGroup = group;
                              deleteModal = true;
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                            aria-label="Delete group"
                          >
                            <TrashIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.delete") }}
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </transition>
                </Menu>
              </template>
            </AutoTable>
          </div>
        </div>
      </main>
    </div>
  </div>

  <Dialog :show="deleteModal" v-if="currentGroup">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.deleteGroup") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.confirmDelete", { name: currentGroup.Name }) }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="focus-visible:outline-[#e05454]-600 inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-start-2"
          @click="deleteGroup()"
        >
          {{ t("msg.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="createModal">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{
          currentGroup == null
            ? t("pages.groups.dialogs.create.createTitle")
            : t("pages.groups.dialogs.create.updateTitle")
        }}
      </DialogTitle>

      <div class="mt-2 text-left">
        <Input
          type="text"
          model-name="Name"
          :model-value="groupName"
          :placeholder="t('pages.groups.dialogs.create.placeholderGroupName')"
          @update:model-value="(input) => (groupName = input)"
          color="rose"
        />
        <br />
        <Input
          type="text"
          model-name="Description"
          :model-value="groupDescription"
          :placeholder="t('pages.groups.dialogs.create.placeholderGroupName')"
          @update:model-value="(input) => (groupDescription = input)"
          color="rose"
        />
        <br />
        <Listbox
          id="groupType"
          model-name="Type"
          :model-title="t('pages.groups.dialogs.create.groupTypeModelTitle')"
          :model-aria-label="t('pages.groups.dialogs.create.groupTypeAriaLabel')"
          :model-list="availableGroupTypes"
          :selected-value="groupType"
          @update:model-value="(val) => (groupType = val)"
          :disabled="currentGroup != null"
        />
        <br />
        <Checklist
          model-name="Knocs"
          :model-value="acls"
          model-type="Description"
          :model-selected="selectedAcls"
          @update:model-value="(selected) => (selectedAcls = selected)"
        />
        <br />
        <Checklist
          v-if="groupType != 'SAML'"
          model-name="Users"
          :model-value="users.filter((user) => user.AuthType !== 'SAML')"
          model-type="Name"
          secondary-type="Company"
          :model-selected="selectedUsers"
          :element-disabled-fn="(u) => u.AuthType == 'saml'"
          :element-title-fn="
            (u) =>
              u.AuthType == 'saml'
                ? t('pages.groups.dialogs.create.usersSamlTitle')
                : t('pages.groups.dialogs.create.usersLocalTitle', { name: u.Name })
          "
          @update:model-value="(selected) => (selectedUsers = selected)"
        />
      </div>

      <div class="flex space-x-3">
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="focus-visible:outline-[#e05454]-600 inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-start-2"
          @click="createGroup()"
        >
          {{ currentGroup == null ? t("msg.create") : t("msg.update") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup>
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { Bars3Icon, ChevronDownIcon, PencilSquareIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import Input from "../../components/Input.vue";
import Listbox from "../../components/Listbox.vue";
import SideBar from "../../components/SideBar.vue";
import Checklist from "../../components/Checklist.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import userService from "../../services/user.service.ts";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../components/ui/dialog";
import { TableCell, TableRow } from "../../components/ui/table";

import globals from "../../globals";
import { useI18n } from "vue-i18n";
import AdminHeader from "../../components/AdminHeader.vue";
import { useUserStore } from "../../pinia/user.ts";
import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";
import AutoTable from "../../components/ui/table/AutoTable.vue";
import { groupTypes } from "../../i18n/helpers.ts";

const { t } = useI18n();

const userStore = useUserStore();

const initialFocus = ref(null);

const errorMessage = ref("");
const infoMessage = ref("");

const search = ref("");
const sidebarOpen = ref(false);
const createModal = ref(false);
const deleteModal = ref(false);

const currentGroup = ref(null);

const availableGroupTypes = ref({
  dynamic: groupTypes(t).dynamic(),
  local: groupTypes(t).local(),
  saml: groupTypes(t).saml(),
});
const groups = ref([]);
const acls = ref();
const users = ref();

const groupName = ref("");
const groupDescription = ref("");
const groupType = ref(groupTypes(t).dynamic());

const selectedAcls = ref([]);
const selectedUsers = ref([]);

const groupsTableColumns = [
  { key: "Name", label: t("fields.groups.name"), sortable: true },
  { key: "Type", label: t("Type"), sortable: true },
  { key: "Description", label: t("fields.groups.description"), sortable: true },
  { key: "Knocs", label: t("Knocs"), sortable: false, cellClass: "text-right", headClass: "text-right" },
  { key: "Users", label: t("fields.groups.users"), sortable: false, cellClass: "text-right", headClass: "text-right" },
  { key: "Edit", hidden: true, cellClass: "text-right", headClass: "text-right" },
];

const filterData = computed(() => {
  if (!groups.value) return [];
  const searchTerm = search.value?.toString().toLocaleLowerCase() ?? "";

  return groups.value.filter((item) => {
    if (!searchTerm) return true;
    return Object.values(item).some((val) => val?.toString().toLocaleLowerCase().includes(searchTerm) ?? false);
  });
});

const closeModal = () => {
  createModal.value = false;
  groupName.value = "";
  groupDescription.value = "";
  selectedAcls.value = [];
  selectedUsers.value = [];
  currentGroup.value = null;
};

const deleteGroup = () => {
  if (currentGroup.value !== null) {
    userService.deleteGroup(currentGroup.value.ID).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.groupDeleted");
      },
      (error) => (errorMessage.value = globals.renderError(t, error)),
    );
  }
  deleteModal.value = false;
  currentGroup.value = null;
};

const openModal = () => {
  if (currentGroup.value !== null) {
    groupName.value = currentGroup.value.Name;
    groupDescription.value = currentGroup.value.Description;
    groupType.value = groupTypes(t)[currentGroup.value.Type]();

    if (currentGroup.value.ACLIDs !== null) {
      for (var aclID of currentGroup.value.ACLIDs) {
        for (var acl of acls.value) {
          if (aclID === acl.ID) {
            selectedAcls.value.push(acl);
            break;
          }
        }
      }
    }

    if (currentGroup.value.UserIDs !== null) {
      for (var userID of currentGroup.value.UserIDs) {
        for (var user of users.value) {
          if (userID === user.ID) {
            selectedUsers.value.push(user);
            break;
          }
        }
      }
    }
  }
  createModal.value = true;
};

const createGroup = () => {
  const data = {
    ID: currentGroup.value?.ID,
    Name: groupName.value,
    Description: groupDescription.value,
    Type: groupType.value.toLowerCase(),
    ACLIDs: [],
    UserIDs: [],
  };

  for (var acl of selectedAcls.value) {
    data.ACLIDs.push(acl.ID);
  }

  for (var user of selectedUsers.value) {
    data.UserIDs.push(user.ID);
  }

  if (currentGroup.value !== null) {
    userService.updateGroup(currentGroup.value.ID, data).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.groupUpdated");
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  } else {
    userService.createGroup(JSON.stringify(data)).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.groupCreated");
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }
  closeModal();
};

const fetchData = () => {
  userStore.fetchGroups({ force: true }).then(
    (response) => {
      groups.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchAcls({ force: true }).then(
    (response) => {
      acls.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchUsers({ force: true }).then(
    (response) => {
      users.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
});
</script>
